import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { ChevronLeft, ChevronRight, Calendar, Download } from 'lucide-react';
import { api } from '../lib/api';

interface AttendanceRecord {
  status: 'present' | 'sick' | 'unknown';
  teacher: string;
  subject: string;
  sessionId: number;
  timestamp: string;
}

interface SessionRecord {
  sessionId: number;
  teacher: string;
  subject: string;
  startTime: string;
  participated: boolean;
}

interface CalendarDay {
  date: string;
  attendance: AttendanceRecord | null;
  sessions: SessionRecord[];
  events: any[];
  override?: 'A' | 'F' | 'E';
}

interface AttendanceHistoryData {
  year: number;
  month: number;
  calendarData: CalendarDay[];
  summary: {
    totalDays: number;
    presentDays: number;
    sickDays: number;
    sessionsParticipated: number;
    totalSessions: number;
  };
}

interface AttendanceCalendarProps {
  studentId: string;
}

const AttendanceCalendar: React.FC<AttendanceCalendarProps> = ({ studentId }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const queryClient = useQueryClient();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1;

  const { data: attendanceData, isLoading, error } = useQuery<AttendanceHistoryData>({
    queryKey: ['student-attendance-history', studentId, currentYear, currentMonth],
    queryFn: async () => {
      const response = await api.get(`/students/${studentId}/attendance-history`, {
        params: { year: currentYear, month: currentMonth }
      });
      return response.data.data;
    },
    enabled: !!studentId,
    retry: 3,
  });

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      if (direction === 'prev') {
        newDate.setMonth(prev.getMonth() - 1);
      } else {
        newDate.setMonth(prev.getMonth() + 1);
      }
      return newDate;
    });
  };

  const getStatusColor = (day: CalendarDay) => {
    // Manual override priority
    if (day.override === 'A') return 'bg-green-100 border-green-300 text-green-800 dark:bg-green-900/30 dark:border-green-800 dark:text-green-200';
    if (day.override === 'F') return 'bg-red-100 border-red-300 text-red-800 dark:bg-red-900/30 dark:border-red-800 dark:text-red-200';
    if (day.override === 'E') return 'bg-blue-100 border-blue-300 text-blue-800 dark:bg-blue-900/30 dark:border-blue-800 dark:text-blue-200';

    if (day.attendance) {
      switch (day.attendance.status) {
        case 'present':
          return 'bg-green-100 border-green-300 text-green-800 dark:bg-green-900/30 dark:border-green-800 dark:text-green-200';
        case 'sick':
          return 'bg-red-100 border-red-300 text-red-800 dark:bg-red-900/30 dark:border-red-800 dark:text-red-200';
        default:
          return 'bg-gray-100 border-gray-300 text-gray-800 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-200';
      }
    }

    // No attendance record = gray
    return 'bg-gray-50 border-gray-200 text-gray-600 dark:bg-gray-900 dark:border-gray-700 dark:text-gray-400';
  };

  const getStatusIcon = (day: CalendarDay) => {
    // Manual override priority
    if (day.override) {
      return day.override;
    }
    
    if (day.attendance) {
      switch (day.attendance.status) {
        case 'present':
          return '✓';
        case 'sick':
          return '✗';
        default:
          return '?';
      }
    }

    return '';
  };

  const formatMonthYear = (date: Date) => {
    return date.toLocaleDateString('de-DE', { 
      year: 'numeric', 
      month: 'long' 
    });
  };

  // Get day of week from date string (0=Sunday, 1=Monday, etc.)
  const getDayOfWeek = (dateStr: string) => {
    const [year, month, day] = dateStr.split('-').map(n => parseInt(n, 10));
    // Use UTC to avoid timezone issues
    const date = new Date(Date.UTC(year, month - 1, day));
    return date.getUTCDay();
  };

  const getWeekdayNames = () => {
    return ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-4 text-xs text-gray-600 dark:text-gray-300">
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
        <span className="ml-2">Lade Anwesenheitsdaten...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center p-4 text-xs text-red-600">
        <p>Fehler beim Laden der Anwesenheitsdaten</p>
      </div>
    );
  }

  if (!attendanceData) {
    return (
      <div className="text-center p-4 text-xs text-gray-600 dark:text-gray-300">
        <p>Keine Anwesenheitsdaten verfügbar</p>
      </div>
    );
  }

  const { calendarData, summary } = attendanceData;

  const updateOverride = async (dateStr: string, status: 'A' | 'F' | 'E') => {
    try {
      console.log('Updating override:', { dateStr, status, studentId });

      // Optimistic update
      queryClient.setQueryData(['student-attendance-history', studentId, currentYear, currentMonth], (oldData: any) => {
        if (!oldData) return oldData;

        const updatedCalendarData = oldData.calendarData.map((day: any) => {
          if (day.date === dateStr) {
            return { ...day, override: status };
          }
          return day;
        });

        return {
          ...oldData,
          calendarData: updatedCalendarData
        };
      });

      await api.put(`/students/${studentId}/attendance-status`, {
        day: dateStr,
        status
      });
      console.log('Override updated successfully');

      // Refetch to ensure consistency
      queryClient.invalidateQueries(['student-attendance-history', studentId, currentYear, currentMonth]);
    } catch (error) {
      console.error('Error updating override:', error);
      // Revert optimistic update on error
      queryClient.invalidateQueries(['student-attendance-history', studentId, currentYear, currentMonth]);
    }
  };

  const downloadPDF = async () => {
    try {
      const response = await api.get(`/students/${studentId}/attendance-pdf`, {
        responseType: 'blob'
      });
      
      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from response headers or create default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `anwesenheit_${studentId}_${currentYear}.pdf`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Fehler beim Herunterladen der PDF-Datei');
    }
  };

  return (
    <div className="space-y-3">
      {/* Header + compact stats */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Calendar className="h-4 w-4 text-primary-600" />
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            {formatMonthYear(currentDate)}
          </h3>
        </div>
        <div className="flex items-center space-x-1">
          <button
            onClick={() => navigateMonth('prev')}
            className="rounded border border-gray-300 p-1 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
            aria-label="Vorheriger Monat"
          >
            <ChevronLeft className="h-3 w-3" />
          </button>
          <button
            onClick={() => navigateMonth('next')}
            className="rounded border border-gray-300 p-1 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
            aria-label="Nächster Monat"
          >
            <ChevronRight className="h-3 w-3" />
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 text-[11px]">
        <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-800 border border-green-200">Anwesend: {summary.presentDays}</span>
        <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-800 border border-red-200">Krank: {summary.sickDays}</span>
        <span className="rounded-full border border-gray-200 bg-gray-100 px-2 py-0.5 text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">Tage: {summary.totalDays}</span>
      </div>

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
        {/* Weekday Headers */}
        <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/80">
          {getWeekdayNames().map(day => (
            <div key={day} className="p-2 text-center text-xs font-medium text-gray-700 dark:text-gray-300">
              {day}
            </div>
          ))}
        </div>
        
        {/* Calendar Days */}
        <div className="grid grid-cols-7">
          {/* Empty cells for days before the first day of the month */}
          {(() => {
            const firstDay = calendarData[0];
            if (!firstDay) return null;
            
            const firstDayOfWeek = getDayOfWeek(firstDay.date);
            const emptyCells = [];
            
            for (let i = 0; i < firstDayOfWeek; i++) {
              emptyCells.push(
                <div key={`empty-${i}`} className="min-h-[56px] border-b border-r border-gray-200 bg-gray-50 p-1 dark:border-gray-700 dark:bg-gray-800/60"></div>
              );
            }
            return emptyCells;
          })()}
          
          {calendarData.map((day) => {
            const dayNumber = parseInt(day.date.split('-')[2], 10);
            const dayOfWeek = getDayOfWeek(day.date);

            return (
              <div
                key={day.date}
                className={`
                  min-h-[56px] border-b border-r border-gray-200 p-1 last:border-r-0 dark:border-gray-700
                  ${getStatusColor(day)}
                  ${dayOfWeek === 0 || dayOfWeek === 6 ? 'bg-gray-50 dark:bg-gray-800/40' : ''}
                  hover:bg-opacity-80 transition-colors
                `}
                title={`${day.date} - ${day.attendance ? `Status: ${day.attendance.status}` : 'Keine Anwesenheit'} - ${day.sessions.length} Veranstaltungen`}
              >
                <div className="flex flex-col h-full">
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="text-xs font-semibold">{dayNumber}</span>
                    <span className="text-[10px] leading-none">{getStatusIcon(day)}</span>
                  </div>
                  <div className="mt-auto flex items-center justify-between gap-1">
                    <div className="inline-flex overflow-hidden rounded border border-gray-300 bg-white/70 dark:border-gray-600 dark:bg-gray-900/80">
                      <button
                        className={`px-1 text-[10px] ${day.override === 'A' ? 'bg-green-200 dark:bg-green-800 dark:text-green-100' : 'dark:text-gray-300'}`}
                        title="Anwesend"
                        onClick={(e) => { e.stopPropagation(); updateOverride(day.date, 'A'); }}
                      >A</button>
                      <button
                        className={`px-1 text-[10px] ${day.override === 'F' ? 'bg-red-200 dark:bg-red-800 dark:text-red-100' : 'dark:text-gray-300'}`}
                        title="Fehlt"
                        onClick={(e) => { e.stopPropagation(); updateOverride(day.date, 'F'); }}
                      >F</button>
                      <button
                        className={`px-1 text-[10px] ${day.override === 'E' ? 'bg-blue-200 dark:bg-blue-800 dark:text-blue-100' : 'dark:text-gray-300'}`}
                        title="Entschuldigt"
                        onClick={(e) => { e.stopPropagation(); updateOverride(day.date, 'E'); }}
                      >E</button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Compact Legend */}
      <div className="rounded bg-gray-50 p-2 dark:bg-gray-800">
        <div className="flex flex-wrap items-center gap-3 text-[11px] text-gray-700 dark:text-gray-300">
          <span className="inline-flex items-center gap-1">
            <span className="w-3 h-3 bg-green-100 border border-green-300 rounded" /> Anwesend
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="w-3 h-3 bg-red-100 border border-red-300 rounded" /> Krank/Fehlt
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="w-3 h-3 bg-blue-100 border border-blue-300 rounded" /> Entschuldigt
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="h-3 w-3 rounded border border-gray-300 bg-gray-100 dark:border-gray-600 dark:bg-gray-700" /> Keine Anwesenheit
          </span>
        </div>
      </div>

      {/* PDF Export Button */}
      <div className="flex justify-center pt-2">
        <button
          onClick={downloadPDF}
          className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
        >
          <Download className="h-4 w-4" />
          12-Monats-Übersicht als PDF
        </button>
      </div>
    </div>
  );
};

export default AttendanceCalendar;
